// ---------------------------------------------------------------------------
// DSS-24: Hide on device toggles — display:none per breakpoint
// ---------------------------------------------------------------------------
import { useCallback } from "react";
import { Eye, EyeOff, Monitor, Tablet, Smartphone } from "lucide-react";
import { BREAKPOINTS } from "./responsive-overrides";

// ── Types ──
export interface DeviceVisibility {
  readonly hideDesktop: boolean;
  readonly hideTablet: boolean;
  readonly hideMobile: boolean;
}

// ── Constants ──
const DEFAULT_VISIBILITY: DeviceVisibility = {
  hideDesktop: false,
  hideTablet: false,
  hideMobile: false,
};

interface DeviceOption {
  readonly key: keyof DeviceVisibility;
  readonly label: string;
  readonly icon: React.ReactNode;
}

const DEVICE_OPTIONS: readonly DeviceOption[] = [
  { key: "hideDesktop", label: "Desktop", icon: <Monitor className="h-3.5 w-3.5" /> },
  { key: "hideTablet", label: "Tablet", icon: <Tablet className="h-3.5 w-3.5" /> },
  { key: "hideMobile", label: "Mobile", icon: <Smartphone className="h-3.5 w-3.5" /> },
];

// ── Props ──
interface DeviceVisibilityProps {
  readonly value: DeviceVisibility;
  readonly onChange: (config: DeviceVisibility) => void;
}

// ── Component ──
export default function DeviceVisibilityControls({ value, onChange }: DeviceVisibilityProps) {
  const config = value ?? DEFAULT_VISIBILITY;

  const handleToggle = useCallback(
    (key: keyof DeviceVisibility) => {
      onChange({ ...config, [key]: !config[key] });
    },
    [config, onChange],
  );

  const hiddenCount = [config.hideDesktop, config.hideTablet, config.hideMobile].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <Eye className="h-3.5 w-3.5" />
          Device Visibility
        </div>
        {hiddenCount > 0 && (
          <span className="rounded-full bg-[var(--color-destructive)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-destructive)]">
            Hidden on {hiddenCount}
          </span>
        )}
      </div>

      {/* Device toggles */}
      <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        {DEVICE_OPTIONS.map((device) => {
          const isHidden = config[device.key];

          return (
            <button
              key={device.key}
              type="button"
              onClick={() => handleToggle(device.key)}
              className={[
                "flex items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                isHidden
                  ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5"
                  : "border-[var(--color-border)] hover:border-[var(--color-primary)]/30",
              ].join(" ")}
            >
              <span className={isHidden ? "text-[var(--color-destructive)]" : "text-[var(--color-foreground)]"}>
                {device.icon}
              </span>
              <span className="flex-1 text-left text-[11px] font-medium text-[var(--color-foreground)]">
                {device.label}
              </span>
              {isHidden ? (
                <EyeOff className="h-3.5 w-3.5 text-[var(--color-destructive)]" />
              ) : (
                <Eye className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
              )}
            </button>
          );
        })}

        {/* Visual indicator */}
        <div className="flex items-center justify-center gap-4 pt-1">
          {DEVICE_OPTIONS.map((device) => {
            const isHidden = config[device.key];
            return (
              <div
                key={device.key}
                className={[
                  "flex flex-col items-center gap-1",
                  isHidden ? "opacity-30" : "opacity-100",
                ].join(" ")}
                title={isHidden ? `Hidden on ${device.label}` : `Visible on ${device.label}`}
              >
                <span className={isHidden ? "text-[var(--color-muted-foreground)]" : "text-[var(--color-primary)]"}>
                  {device.icon}
                </span>
                <span className="text-[8px] text-[var(--color-muted-foreground)]">
                  {isHidden ? "Hidden" : "Visible"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* CSS preview */}
      {hiddenCount > 0 && (
        <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
          <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
            {generateVisibilityCss(config, ".selector")}
          </code>
        </div>
      )}
    </div>
  );
}

// ── CSS generator ──
export function generateVisibilityCss(config: DeviceVisibility, selector: string): string {
  const lines: string[] = [];

  if (config.hideDesktop) {
    lines.push(`@media (min-width: ${BREAKPOINTS.desktop.minWidth}px) {`);
    lines.push(`  ${selector} { display: none !important; }`);
    lines.push("}");
  }

  if (config.hideTablet) {
    if (lines.length > 0) lines.push("");
    lines.push(`@media (min-width: ${BREAKPOINTS.tablet.minWidth}px) and (max-width: ${BREAKPOINTS.tablet.maxWidth}px) {`);
    lines.push(`  ${selector} { display: none !important; }`);
    lines.push("}");
  }

  if (config.hideMobile) {
    if (lines.length > 0) lines.push("");
    lines.push(`@media (max-width: ${BREAKPOINTS.mobile.maxWidth}px) {`);
    lines.push(`  ${selector} { display: none !important; }`);
    lines.push("}");
  }

  return lines.length > 0 ? lines.join("\n") : "/* Visible on all devices */";
}
